import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { reportsApi, establishmentsApi } from '@/lib/api';
import { FileText, Plus, Loader2, Target, ChevronRight, AlertCircle, Users, CheckCircle, Eye, EyeOff, Send } from 'lucide-react';

const MONTHS = [
  { value: '01', label: 'Janvier' }, { value: '02', label: 'Février' }, { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' }, { value: '05', label: 'Mai' }, { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' }, { value: '08', label: 'Août' }, { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' }, { value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' },
];

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [showGenForm, setShowGenForm] = useState(false);
  const [genForm, setGenForm] = useState({ establishment_id: '', period: '2026-01' });
  const [error, setError] = useState('');
  const [genAllResult, setGenAllResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [r, e] = await Promise.all([reportsApi.list(), establishmentsApi.list()]);
      setReports(r.data);
      const active = e.data.filter(x => !x.is_demo);
      setEstablishments(active);
      if (active.length > 0) setGenForm(f => ({ ...f, establishment_id: active[0].id }));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleGenerate = async () => {
    if (!genForm.establishment_id) return;
    setGenerating(true);
    setError('');
    try {
      const res = await reportsApi.generate(genForm);
      navigate(`/reports/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la génération');
    }
    setGenerating(false);
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    setError('');
    setGenAllResult(null);
    try {
      const res = await reportsApi.generateAll({ period: genForm.period });
      setGenAllResult(res.data);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la génération groupée');
    }
    setGeneratingAll(false);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600 bg-emerald-50';
    if (score >= 45) return 'text-[#B8860B] bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const handlePublish = async (reportId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await reportsApi.publish(reportId);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la publication');
    }
  };

  const handleUnpublish = async (reportId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Retirer ce rapport de l\'espace client ?')) return;
    try {
      await reportsApi.unpublish(reportId);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Rapports</h1>
          <p className="text-gray-400 mt-1 text-sm">Générez, vérifiez et publiez les rapports de benchmark</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowGenForm(!showGenForm)}
            className="bg-[#B8860B] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#9A7009] transition-all shadow-md flex items-center gap-2"
            data-testid="generate-report-btn"
          >
            <Target size={16} /> Générer un rapport
          </button>
        </div>
      </div>

      {/* Status filter */}
      {reports.length > 0 && (
        <div className="flex gap-2 items-center" data-testid="reports-status-filter">
          <span className="text-xs text-gray-400 font-medium">Filtrer :</span>
          {[
            { key: 'all', label: 'Tous', count: reports.length },
            { key: 'draft', label: 'Brouillons', count: reports.filter(r => r.status !== 'published').length },
            { key: 'published', label: 'Publiés', count: reports.filter(r => r.status === 'published').length },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${statusFilter === f.key ? 'bg-[#1B2A4A] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`filter-status-${f.key}`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      )}

      {showGenForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6" data-testid="generate-report-form">
          <h2 className="font-bold text-[#1B2A4A] font-manrope mb-4">Nouveau rapport</h2>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2" data-testid="generate-report-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Établissement</label>
              <select
                data-testid="generate-report-establishment"
                value={genForm.establishment_id}
                onChange={e => setGenForm({ ...genForm, establishment_id: e.target.value })}
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
              >
                {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Période</label>
              <select
                data-testid="generate-report-period"
                value={genForm.period}
                onChange={e => setGenForm({ ...genForm, period: e.target.value })}
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
              >
                {MONTHS.map(m => <option key={m.value} value={`2026-${m.value}`}>{m.label} 2026</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerate}
                disabled={generating || !genForm.establishment_id}
                className="w-full h-10 bg-[#1B2A4A] text-white rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-report-submit"
              >
                {generating ? <><Loader2 size={16} className="animate-spin" /> En cours...</> : <><Target size={16} /> Générer</>}
              </button>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerateAll}
                disabled={generatingAll}
                className="w-full h-10 bg-[#B8860B] text-white rounded-lg font-semibold text-sm hover:bg-[#9A7009] transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-all-btn"
              >
                {generatingAll ? <><Loader2 size={16} className="animate-spin" /> Génération...</> : <><Users size={16} /> Tous les clients</>}
              </button>
            </div>
          </div>
          {generating && <p className="text-xs text-gray-400 mt-3">Génération du diagnostic en cours... Le rapport sera en brouillon jusqu'à publication.</p>}

          {genAllResult && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-100" data-testid="generate-all-results">
              <p className="text-sm font-bold text-[#1B2A4A] mb-2">Résultat de la génération groupée ({genAllResult.period})</p>
              <div className="space-y-1">
                {genAllResult.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {r.status === 'generated' && <CheckCircle size={12} className="text-emerald-500" />}
                    {r.status === 'exists' && <CheckCircle size={12} className="text-gray-400" />}
                    {r.status === 'skipped' && <AlertCircle size={12} className="text-amber-500" />}
                    {r.status === 'error' && <AlertCircle size={12} className="text-red-500" />}
                    <span className="font-medium text-[#1B2A4A]">{r.establishment}</span>
                    <span className="text-gray-400">
                      {r.status === 'generated' && 'Généré'}
                      {r.status === 'exists' && 'Déjà existant'}
                      {r.status === 'skipped' && r.reason}
                      {r.status === 'error' && r.reason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {reports.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FileText size={40} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Aucun rapport</p>
          <p className="text-gray-400 text-sm mt-1">Générez votre premier rapport de benchmark</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.filter(r => statusFilter === 'all' ? true : statusFilter === 'published' ? r.status === 'published' : r.status !== 'published').map((r) => (
            <div
              key={r.id}
              className="flex items-center bg-white rounded-xl border border-gray-100 shadow-[0_2px_8px_rgba(27,42,74,0.04)] px-6 py-4 hover:shadow-md transition-all group"
              data-testid={`report-item-${r.id}`}
            >
              <Link to={`/reports/${r.id}`} className="flex items-center flex-1 min-w-0">
                <div className="w-12 h-12 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center mr-4">
                  <FileText size={20} className="text-[#1B2A4A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#1B2A4A] font-manrope truncate">{r.establishment_name}</p>
                  <p className="text-sm text-gray-400">{r.period_label} — {r.segment_label}</p>
                </div>
              </Link>
              <div className="flex items-center gap-3">
                {/* Status badge */}
                {r.status === 'published' ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`report-status-${r.id}`}>
                    <Eye size={12} /> Publié
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid={`report-status-${r.id}`}>
                    <EyeOff size={12} /> Brouillon
                  </span>
                )}
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(r.diagnostic?.score_global || 0)}`}>
                  {r.diagnostic?.score_global || 0}/100
                </div>
                {r.diagnostic?.cout_inaction > 0 && (
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-400">Coût d'inaction</p>
                    <p className="text-sm font-bold text-red-600">{Number(r.diagnostic.cout_inaction).toLocaleString('fr-FR')} €/mois</p>
                  </div>
                )}
                {/* Publish / Unpublish button */}
                {r.status === 'published' ? (
                  <button
                    onClick={(e) => handleUnpublish(r.id, e)}
                    className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                    title="Retirer de l'espace client"
                    data-testid={`unpublish-btn-${r.id}`}
                  >
                    <EyeOff size={16} />
                  </button>
                ) : (
                  <button
                    onClick={(e) => handlePublish(r.id, e)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B2A4A] text-white text-xs font-semibold hover:bg-[#152039] transition-all shadow-sm"
                    title="Publier vers le client"
                    data-testid={`publish-btn-${r.id}`}
                  >
                    <Send size={12} /> Publier
                  </button>
                )}
                <Link to={`/reports/${r.id}`}>
                  <ChevronRight size={18} className="text-gray-300 group-hover:text-[#B8860B] transition-colors" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
